import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { NotAvailableState } from "@/components/ui/system-state";

/**
 * System Status tile — design port (ALL-5825, dashboard lane task 5).
 *
 * The prototype tile takes `items: SystemStatusItem[]` (from
 * `getSystemStatusItems()`) and `alerts: MonitoringAlert[]`, and additionally
 * reads `BROADCAST_STATS` / `BROADCAST_ROWS` directly from
 * `features/oversight/oversight-data`: three status rows, an open-alert list
 * with severities, a broadcast pipeline meter (Confirmed / In-Flight / Queued /
 * Stuck / Failed) and the latest broadcasts.
 *
 * Every one of those is a fixture. This app has no monitoring, chain-health,
 * alerting or broadcast-state endpoint — `src/lib/api.ts` carries no
 * `/v1/monitoring/*` or `/v1/chain/*` route, and the aggregate `/monitoring`
 * page and its children are `DeferredPage`s for exactly this reason
 * (`dashboard-selectors.ts` records the same decision on the attention bar).
 *
 * A status dot reading "Operational" or a "3 confirmed, 1 stuck" pipeline is an
 * operational claim with no source, so this tile keeps the designed header and
 * renders the designed not-available state. Nothing here is estimated, and no
 * count, badge or status colour is shown.
 *
 * The header link is kept as designed: it points at the deferred `/monitoring`
 * overview, and the not-available body guards the tile from reading as live
 * system state.
 */
export function SystemStatus() {
  return (
    <div className="flex h-full flex-col">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h2 className="text-sm font-bold uppercase tracking-[0.06em] text-muted">System Status</h2>
        <Link
          to="/monitoring"
          className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-primary hover:underline"
        >
          View all <ArrowUpRight className="size-3.5" aria-hidden />
        </Link>
      </div>
      <NotAvailableState
        className="flex-1 justify-center"
        title="System status is not available yet"
        description="Service status, open alerts and the broadcast pipeline are not collected in this environment, so no status rows, alert list or pipeline meter are shown here."
        reason="No gateway route serves monitoring, alert or broadcast state — there is no /v1/monitoring/* or /v1/chain/* route in this app."
      />
    </div>
  );
}
